/**
 * Tank Royale audio. Web Audio API synth (no external samples). Lazy
 * AudioContext, master gain, mute toggle persisted to localStorage.
 *
 * Browsers refuse to start audio until a user gesture; init() is
 * called from the first input on the host page (any keypress / click).
 * Until then play* fns are silent no-ops.
 */

const LS_MUTE_KEY = 'tank:mute';
const MASTER_GAIN = 0.16;

let ctx: AudioContext | null = null;
let audioPaused = false;
export function setAudioPaused(next: boolean): void {
  audioPaused = next;
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}

let master: GainNode | null = null;
let muted = readMutedInitial();

function readMutedInitial(): boolean {
  try { return localStorage.getItem(LS_MUTE_KEY) === '1'; }
  catch { return false; }
}

export function isMuted(): boolean { return muted; }

export function setMuted(next: boolean): void {
  muted = next;
  try { localStorage.setItem(LS_MUTE_KEY, next ? '1' : '0'); }
  catch { /* ignore */ }
  if (master) master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
}

export function toggleMuted(): boolean { setMuted(!muted); return muted; }

/** Must be called inside a user gesture. Safe to call repeatedly —
 *  later calls are no-ops once context exists. */
export function initAudio(): void {
  if (ctx) { void ctx.resume().catch(() => {}); return; }
  try {
    const C = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    ctx = new C();
    master = ctx.createGain();
    master.gain.value = muted || audioPaused ? 0 : MASTER_GAIN;
    master.connect(ctx.destination);
    startMusic();
  } catch { ctx = null; master = null; }
}

/** A simple tone with ADSR-ish envelope. */
function tone(opts: {
  freq: number;
  duration: number;
  type?: OscillatorType;
  attack?: number;
  release?: number;
  gain?: number;
  detune?: number;
  sweepTo?: number;          // optional target freq for an exponential ramp
}): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  if (opts.detune) osc.detune.value = opts.detune;
  if (opts.sweepTo) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.sweepTo), now + opts.duration);
  }
  const g = ctx.createGain();
  const attack = opts.attack ?? 0.005;
  const release = opts.release ?? Math.min(0.12, opts.duration);
  const peak = opts.gain ?? 0.6;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
  osc.connect(g).connect(master);
  osc.start(now);
  osc.stop(now + opts.duration + release + 0.02);
}

/** A noise burst — useful for hits / explosions. */
function noise(opts: {
  duration: number;
  filterFreq?: number;
  filterQ?: number;
  sweepTo?: number;
  gain?: number;
}): void {
  if (!ctx || !master) return;
  const now = ctx.currentTime;
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * opts.duration), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = opts.filterFreq ?? 800;
  filter.Q.value = opts.filterQ ?? 1;
  if (opts.sweepTo) {
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, opts.sweepTo), now + opts.duration);
  }
  const g = ctx.createGain();
  g.gain.setValueAtTime(opts.gain ?? 0.5, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + opts.duration);
  src.connect(filter).connect(g).connect(master);
  src.start(now);
  src.stop(now + opts.duration + 0.02);
}

// ── Game-specific SFX ─────────────────────────────────────────────

export function sfxShoot(): void {
  tone({ freq: 1100, sweepTo: 240, duration: 0.12, type: 'square', gain: 0.18, release: 0.04 });
}

export function sfxHit(): void {
  noise({ duration: 0.18, filterFreq: 1400, sweepTo: 200, gain: 0.55 });
}

export function sfxKill(): void {
  noise({ duration: 0.55, filterFreq: 900, sweepTo: 60, gain: 0.80 });
  tone({ freq: 80, sweepTo: 30, duration: 0.4, type: 'sawtooth', gain: 0.45, release: 0.1 });
}

export function sfxShield(): void {
  tone({ freq: 320, sweepTo: 1100, duration: 0.30, type: 'triangle', gain: 0.32, release: 0.05 });
}

export function sfxWin(): void {
  // Major chord arpeggio.
  const root = 440;
  for (const [note, delay] of [[1, 0], [1.25, 0.10], [1.5, 0.20], [2, 0.30]] as const) {
    setTimeout(() => tone({ freq: root * note, duration: 0.45, type: 'triangle', gain: 0.30, release: 0.18 }), delay * 1000);
  }
}

export function sfxLose(): void {
  // Falling minor sigh.
  for (const [note, delay] of [[1, 0], [0.84, 0.16], [0.66, 0.34]] as const) {
    setTimeout(() => tone({ freq: 440 * note, duration: 0.45, type: 'triangle', gain: 0.24, release: 0.18 }), delay * 1000);
  }
}

// ── Background music ──────────────────────────────────────────────
//
// Tense, minor-key arpeggio that loops while the AudioContext is open.
// Scheduled in 300ms-lookahead windows so the loop stays sample-
// accurate even if the main thread stutters. Gain is very low so SFX
// always read over the top. Mute toggles the master gain, so music
// silences with everything else.

interface MusicNote {
  freq: number;
  duration: number;
  type: OscillatorType;
  gain: number;
  filter?: number;
}
const TANK_PATTERN: MusicNote[] = [
  { freq: 110.0, duration: 0.50, type: 'sawtooth',  gain: 0.40, filter: 600 },
  { freq: 164.8, duration: 0.25, type: 'triangle',  gain: 0.30 },
  { freq: 196.0, duration: 0.25, type: 'triangle',  gain: 0.30 },
  { freq: 220.0, duration: 0.50, type: 'sawtooth',  gain: 0.36, filter: 700 },
  { freq: 164.8, duration: 0.25, type: 'triangle',  gain: 0.28 },
  { freq: 246.9, duration: 0.25, type: 'triangle',  gain: 0.30 },
  { freq: 110.0, duration: 0.50, type: 'sawtooth',  gain: 0.40, filter: 600 },
  { freq: 196.0, duration: 0.25, type: 'triangle',  gain: 0.30 },
  { freq: 246.9, duration: 0.25, type: 'triangle',  gain: 0.34 },
  { freq: 196.0, duration: 0.25, type: 'triangle',  gain: 0.30 },
  { freq: 164.8, duration: 0.25, type: 'triangle',  gain: 0.28 },
  { freq: 130.8, duration: 0.50, type: 'sawtooth',  gain: 0.36, filter: 550 },
];
const MUSIC_GAIN = 0.07;

let musicBus: GainNode | null = null;
let musicIx = 0;
let musicNextStart = 0;
let musicHandle: number | null = null;

function scheduleMusicNote(note: MusicNote, when: number): void {
  if (!ctx || !musicBus) return;
  const osc = ctx.createOscillator();
  osc.type = note.type;
  osc.frequency.value = note.freq;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(note.gain * MUSIC_GAIN, when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, when + note.duration);
  if (note.filter && note.filter > 0) {
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = note.filter;
    osc.connect(f).connect(g).connect(musicBus);
  } else {
    osc.connect(g).connect(musicBus);
  }
  osc.start(when);
  osc.stop(when + note.duration + 0.05);
}

function musicTick(): void {
  if (!ctx || !musicBus) return;
  const lookahead = 0.3;
  while (musicNextStart < ctx.currentTime + lookahead) {
    const note = TANK_PATTERN[musicIx % TANK_PATTERN.length]!;
    scheduleMusicNote(note, musicNextStart);
    musicNextStart += note.duration;
    musicIx += 1;
  }
  musicHandle = window.setTimeout(musicTick, 100);
}

/** Kick off the looping background pattern. Idempotent — safe to call
 *  every time initAudio fires. Opens with a 4-second filter + gain
 *  swell so the music fades up rather than slamming in. */
export function startMusic(): void {
  if (!ctx || !master) return;
  if (musicBus) return;
  const now = ctx.currentTime;
  musicBus = ctx.createGain();
  // Gain swell — 0 → 1 over 3.5s with a slight curve at the end.
  musicBus.gain.setValueAtTime(0.0001, now);
  musicBus.gain.exponentialRampToValueAtTime(1.0, now + 3.5);
  // Low-pass introduction — opens from 400 Hz to 6 kHz over 4s for a
  // muffled-to-bright swell.
  const introFilter = ctx.createBiquadFilter();
  introFilter.type = 'lowpass';
  introFilter.frequency.setValueAtTime(400, now);
  introFilter.frequency.exponentialRampToValueAtTime(6000, now + 4);
  musicBus.connect(introFilter).connect(master);
  musicIx = 0;
  musicNextStart = now + 0.1;
  musicTick();
}

export function stopMusic(): void {
  if (musicHandle !== null) { clearTimeout(musicHandle); musicHandle = null; }
  if (musicBus) { try { musicBus.disconnect(); } catch { /* ignore */ } }
  musicBus = null;
}

// ── Low-HP heartbeat ──────────────────────────────────────────────
//
// Persistent sub-bass thump that engages whenever any human tank is
// at 1 HP. Tempo accelerates with how close to death we are (1 HP
// always pulses at the fastest rate). Stops cleanly on stopHeartbeat.

let heartbeatActive = false;
let heartbeatHandle: number | null = null;
const HEARTBEAT_PERIOD_S = 0.62;

function heartbeatBeat(): void {
  if (!ctx || !master || !heartbeatActive) return;
  const now = ctx.currentTime;
  // Two-tap "lub-dub": a deep boom then a softer echo.
  const beat = (when: number, freq: number, gain: number, dur: number) => {
    if (!ctx || !master) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * 1.8, when);
    osc.frequency.exponentialRampToValueAtTime(freq, when + dur * 0.6);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(gain, when + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(g).connect(master);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  };
  beat(now,           58, 0.55, 0.32);
  beat(now + 0.14,    52, 0.30, 0.28);
  heartbeatHandle = window.setTimeout(heartbeatBeat, HEARTBEAT_PERIOD_S * 1000);
}

export function startHeartbeat(): void {
  if (heartbeatActive) return;
  heartbeatActive = true;
  heartbeatBeat();
}

export function stopHeartbeat(): void {
  heartbeatActive = false;
  if (heartbeatHandle !== null) { clearTimeout(heartbeatHandle); heartbeatHandle = null; }
}
